class ProductTabs extends HTMLElement {
  connectedCallback() {
    this.navWrapper = this.querySelector('.product-tabs__nav-wrapper');
    this.tabs = Array.from(this.querySelectorAll('[data-tab-target]'));
    this.panels = this.tabs.map((tab) => this.getPanelById(tab.dataset.tabTarget)).filter(Boolean);
    this.header = document.querySelector('.section-header');

    if (!this.navWrapper || this.tabs.length === 0 || this.panels.length === 0) return;

    this.onTabClick = this.onTabClick.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.updateHeaderOffset = this.updateHeaderOffset.bind(this);

    this.ticking = false;
    this.hasScrolled = false;

    this.tabs.forEach((tab) => tab.addEventListener('click', this.onTabClick));
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);

    if (this.header) {
      this.headerObserver = new MutationObserver(this.updateHeaderOffset);
      this.headerObserver.observe(this.header, { attributes: true, attributeFilter: ['class', 'style'] });
      this.header.addEventListener('transitionrun', this.updateHeaderOffset);
      this.header.addEventListener('transitionend', this.updateHeaderOffset);
    }

    this.setNavHeight();
    this.updateHeaderOffset();
    this.setActiveTab(this.tabs[0], false);

    if (this.syncFromHash()) return;
    if (window.scrollY > 0) {
      this.updateActiveFromScroll();
    }
  }

  disconnectedCallback() {
    this.tabs?.forEach((tab) => tab.removeEventListener('click', this.onTabClick));
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    this.headerObserver?.disconnect();
    this.header?.removeEventListener('transitionrun', this.updateHeaderOffset);
    this.header?.removeEventListener('transitionend', this.updateHeaderOffset);
  }

  onResize() {
    this.setNavHeight();
    this.updateHeaderOffset();
    if (this.hasScrolled || window.scrollY > 0) {
      this.updateActiveFromScroll();
    }
  }

  onScroll() {
    this.hasScrolled = true;
    if (this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(() => {
      this.updateHeaderOffset();
      this.updateActiveFromScroll();
      this.ticking = false;
    });
  }

  onTabClick(event) {
    event.preventDefault();
    const tab = event.currentTarget;
    const panel = this.getPanelById(tab.dataset.tabTarget);
    if (!panel) return;

    this.setActiveTab(tab, true);
    this.scrollToPanel(panel, true);

    if (history.replaceState) {
      history.replaceState(null, '', `#${panel.id}`);
    }
  }

  syncFromHash() {
    const rawHash = window.location.hash.replace('#', '');
    if (!rawHash) return false;

    const panel = this.getPanelById(decodeURIComponent(rawHash));
    if (!panel) return false;

    const linkedTab = this.tabs.find((tab) => tab.dataset.tabTarget === panel.id);
    if (!linkedTab) return false;

    this.setActiveTab(linkedTab, false);

    window.requestAnimationFrame(() => {
      this.scrollToPanel(panel, false);
    });

    return true;
  }

  setNavHeight() {
    this.style.setProperty('--product-tabs-nav-height', `${this.navWrapper.offsetHeight}px`);
  }

  getPanelById(id) {
    if (!id) return null;
    return this.querySelector(`#${id}`);
  }

  getPanelTop(panel) {
    return panel.getBoundingClientRect().top + window.scrollY;
  }

  scrollToPanel(panel, smooth) {
    const top = this.getPanelTop(panel) - this.getScrollOffset();
    window.scrollTo({ top, behavior: smooth ? 'smooth' : 'auto' });
  }

  updateHeaderOffset() {
    const rootStyles = getComputedStyle(document.documentElement);
    const fallbackHeaderHeight = parseFloat(rootStyles.getPropertyValue('--header-height')) || 0;

    if (!this.header) {
      this.currentHeaderVisibleHeight = fallbackHeaderHeight;
      this.style.setProperty('--product-tabs-header-visible-height', `${fallbackHeaderHeight}px`);
      return;
    }

    const rect = this.header.getBoundingClientRect();
    const visibleHeight = Math.max(0, Math.min(rect.bottom, rect.height || fallbackHeaderHeight));

    this.currentHeaderVisibleHeight = visibleHeight;
    this.style.setProperty('--product-tabs-header-visible-height', `${visibleHeight}px`);
  }

  getScrollOffset() {
    const headerHeight = this.currentHeaderVisibleHeight || 0;
    const stickyOffset = parseFloat(this.dataset.stickyOffset || '0') || 0;
    const navHeight = this.navWrapper?.offsetHeight || 0;
    return headerHeight + stickyOffset + navHeight + 12;
  }

  updateActiveFromScroll() {
    const marker = window.scrollY + this.getScrollOffset();
    const firstPanelTop = this.getPanelTop(this.panels[0]);

    if (marker <= firstPanelTop + 2) {
      this.setActiveTab(this.tabs[0], false);
      return;
    }

    let activeIndex = 0;

    this.panels.forEach((panel, index) => {
      if (this.getPanelTop(panel) <= marker + 2) {
        activeIndex = index;
      }
    });

    this.setActiveTab(this.tabs[activeIndex], false);
  }

  setActiveTab(activeTab, centerTab) {
    if (!activeTab) return;

    this.tabs.forEach((tab) => {
      const isActive = tab === activeTab;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    if (centerTab) {
      activeTab.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }
}

if (!customElements.get('product-tabs')) {
  customElements.define('product-tabs', ProductTabs);
}
